
import React, { useEffect, useState } from "react";
import indianNumberFormatComponent from "@/common/helpers/indianNumberFormatComponent";
import Dropdown from "@/common/components/dropdown/dropdown";
import { Button, Checkbox, Chip, CircularProgress, IconButton } from "@mui/material";
import TextInput from "@/common/components/text-input/text-input";
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

/* -------------------------------- HELPERS -------------------------------- */
const getCId = (row) => row?.customerCode || row?.customer_code || "";
const getCName = (row) => row?.customerName || row?.customer_name || "";

/* ------------------------------------------------------------------
   ✅ HELPER: Calculate Eligibility & Row Style (WITH HOVER)
------------------------------------------------------------------ */

const parseValue = (val) => {
  if (val === null || val === undefined) return {};

  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return parseFloat(val) || {};
    }
  }
  return val || {};
};

const getSum = (obj) => {
  const parsed = parseValue(obj);
  if (typeof parsed !== "object") return parseFloat(parsed) || 0;

  return Object.values(parsed).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0
  );
};

const getCategoryValue = (obj, cat) => {
  const parsed = parseValue(obj);
  if (typeof parsed !== "object") return 0;
  return parseFloat(parsed?.[cat] || 0);
};

const isMaidaEligible = (salesObj = {}, targetObj = {}, isMaidaAdditional = false) => {
  const cats = ["PA", "PB", "BA", "BB", "MUL"];
  return cats.some((cat) => {
    const sales = parseFloat(salesObj?.[cat] || 0);
    const target = parseFloat(targetObj?.[cat] || 0);
    if (isMaidaAdditional) {
      return sales > target;
    }
    return target > 0 && sales >= target;
  });
};

const vadDataUpdate = (o, i) => (i === undefined ? o : i);


/* ------------------------------------------------------------------
   ✅ MINI-COMPONENTS: Editable Cells with Local State
------------------------------------------------------------------ */

const EditableRateInput = ({ rowData, cat, realIndex, formik, isMaidaAdditional = false }) => {
  const parsedNewRates = typeof parseValue(rowData.new_scheme_rs_per_kg) === 'object' && Object.keys(parseValue(rowData.new_scheme_rs_per_kg)).length > 0
    ? parseValue(rowData.new_scheme_rs_per_kg) : null;
  const ratesObj = parsedNewRates || parseValue(rowData.schemeRsPerKg);
  const initialRate = getCategoryValue(ratesObj, cat);

  const [val, setVal] = useState(initialRate === 0 ? "" : initialRate);

  useEffect(() => {
    setVal(initialRate === 0 ? "" : initialRate);
  }, [initialRate]);

  const handleBlur = () => {
    const newRate = parseFloat(val) || 0;
    const salesObj = parseValue(rowData.monthSales);
    const targetObj = parseValue(rowData.targetMts);
    const sales = parseFloat(salesObj[cat]) || 0;
    const target = parseFloat(targetObj[cat]) || 0;

    let newAmount = 0;
    if (isMaidaAdditional) {
      // Maida Additional: Pay on Target ONLY if Sales exceed Target
      newAmount = (sales > target) ? Math.round(target * newRate * 1000) : 0;
    } else {
      // Standard Maida: Pay on Actual Sales if Sales > 0
      newAmount = (sales > 0) ? Math.round(sales * newRate * 1000) : 0;
    }

    const parsedNewAmts = typeof parseValue(rowData.new_scheme_amount) === 'object' && Object.keys(parseValue(rowData.new_scheme_amount)).length > 0
      ? parseValue(rowData.new_scheme_amount) : null;
    const amountsObj = parsedNewAmts || parseValue(rowData.categoryEligibleAmounts);

    const updatedRates = { ...ratesObj, [cat]: newRate };
    const updatedAmounts = { ...amountsObj, [cat]: newAmount };

    formik.setFieldValue(`childData[${realIndex}].new_scheme_rs_per_kg`, updatedRates);
    formik.setFieldValue(`childData[${realIndex}].new_scheme_amount`, updatedAmounts);
    formik.setFieldValue(`childData[${realIndex}].schemeRsPerKg`, updatedRates); // For UI update if needed
    formik.setFieldValue(`childData[${realIndex}].categoryEligibleAmounts`, updatedAmounts); // For UI update if needed
  };

  return (
    <input
      type="number"
      step="any"
      value={val}
      placeholder="0"
      onChange={(e) => setVal(e.target.value)}
      onBlur={handleBlur}
      style={{
        width: "65px",
        textAlign: "right",
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: "4px",
        outline: "none",
      }}
    />
  );
};

const EditableAmountInput = ({ rowData, cat, realIndex, formik, isMaidaAdditional = false }) => {
  const parsedNewAmts = typeof parseValue(rowData.new_scheme_amount) === 'object' && Object.keys(parseValue(rowData.new_scheme_amount)).length > 0
    ? parseValue(rowData.new_scheme_amount) : null;
  const amountsObj = parsedNewAmts || parseValue(rowData.categoryEligibleAmounts);
  const initialAmount = getCategoryValue(amountsObj, cat);

  const [val, setVal] = useState(initialAmount === 0 ? "" : initialAmount);

  useEffect(() => {
    setVal(initialAmount === 0 ? "" : initialAmount);
  }, [initialAmount]);

  const handleBlur = () => {
    const newAmount = parseFloat(val) || 0;
    const salesObj = parseValue(rowData.monthSales);
    const targetObj = parseValue(rowData.targetMts);
    const sales = parseFloat(salesObj[cat]) || 0;
    const target = parseFloat(targetObj[cat]) || 0;

    let newRate = 0;
    if (isMaidaAdditional) {
      // Maida Additional: Rate = Amount / (Target * 1000)
      if (target > 0 && sales > target) {
        newRate = newAmount / (target * 1000);
      }
    } else {
      // Standard Maida: Rate = Amount / (Sales * 1000)
      if (sales > 0) {
        newRate = newAmount / (sales * 1000);
      }
    }

    const parsedNewRates = typeof parseValue(rowData.new_scheme_rs_per_kg) === 'object' && Object.keys(parseValue(rowData.new_scheme_rs_per_kg)).length > 0
      ? parseValue(rowData.new_scheme_rs_per_kg) : null;
    const ratesObj = parsedNewRates || parseValue(rowData.schemeRsPerKg);

    const updatedAmounts = { ...amountsObj, [cat]: newAmount };
    const updatedRates = {
      ...ratesObj, [cat]:
        Number(newRate.toFixed(2))
    };

    formik.setFieldValue(`childData[${realIndex}].new_scheme_amount`, updatedAmounts);
    formik.setFieldValue(`childData[${realIndex}].new_scheme_rs_per_kg`, updatedRates);
    formik.setFieldValue(`childData[${realIndex}].categoryEligibleAmounts`, updatedAmounts); // For UI update if needed
    formik.setFieldValue(`childData[${realIndex}].schemeRsPerKg`, updatedRates); // For UI update if needed
  };

  return (
    <input
      type="number"
      step="any"
      value={val}
      placeholder="0"
      onChange={(e) => setVal(e.target.value)}
      onBlur={handleBlur}
      style={{
        width: "80px",
        textAlign: "right",
        border: "1px solid #100979ff",
        borderRadius: "4px",
        padding: "4px",
        color: "#100979ff",
        fontWeight: 600,
        outline: "none",
      }}
    />
  );
};

// 👇 Dropdown specifically for handling the Feed Slab & Auto-Updating Costs
const EditableSlabDropdown = ({ rowData, realIndex, formik, setEditingRowId }) => {
  const allSlabs = rowData.allSlab || [];
  const allCosts = rowData.allCostPerTon || [];

  const from = rowData.newAchievedSlabFrom !== undefined ? rowData.newAchievedSlabFrom : (rowData.achievedSlabFrom || 0);
  const to = rowData.newAchievedSlabTo !== undefined ? rowData.newAchievedSlabTo : (rowData.achievedSlabTo || 0);
  const currentSlab = `${from}-${to}`;

  const slabOptions = [];
  const seen = new Set();
  allSlabs.forEach((slab, i) => {
    if (!seen.has(slab)) {
      seen.add(slab);
      slabOptions.push({ slab, cost: allCosts[i] });
    }
  });

  const handleChange = (e) => {
    const selectedSlabStr = e.target.value;
    const selectedOption = slabOptions.find(o => o.slab === selectedSlabStr);

    if (selectedOption) {
      const newCost = selectedOption.cost || 0;
      let newFrom = 0;
      let newTo = 0;

      if (selectedSlabStr.includes("-")) {
        [newFrom, newTo] = selectedSlabStr.split("-").map(Number);
      }

      const salesVolume = getSum(rowData.monthSales);
      const newAmount = Math.round(salesVolume * newCost);

      formik.setFieldValue(`childData[${realIndex}].newAchievedSlabFrom`, newFrom);
      formik.setFieldValue(`childData[${realIndex}].newAchievedSlabTo`, newTo);
      formik.setFieldValue(`childData[${realIndex}].new_scheme_rs_per_ton`, newCost);
      formik.setFieldValue(`childData[${realIndex}].new_scheme_amount`, newAmount);
    }
    setEditingRowId(null);
  };

  return (
    <select
      value={currentSlab}
      onChange={handleChange}
      onBlur={() => setEditingRowId(null)}
      autoFocus
      style={{
        padding: "4px",
        borderRadius: "4px",
        outline: "none",
        border: "2px solid #100979ff",
        width: "110px",
        fontWeight: 600,
        color: "#100979ff"
      }}
    >
      {slabOptions.map((opt, i) => (
        <option key={i} value={opt.slab}>{opt.slab}</option>
      ))}
    </select>
  );
};

/* -------------------------------- MAIN -------------------------------- */

export const commonColumns = ({
  handleActionChange,
  actionOptions,
  handleAvgRuleChange,
  formik,
  averageRuleOptions,
  handleButtonClick,
  productName,
  filteredData,
  editingRowId,
  setEditingRowId
}) => {
  const nameToTest = String(
    productName ||
    formik?.values?.schemeName ||
    formik?.values?.productName ||
    formik?.values?.productId?.label ||
    formik?.values?.productId?.dropdown_list_name ||
    ""
  ).toLowerCase();
  const isMaida = nameToTest.includes("maida");
  const isMaidaAdditional = nameToTest.includes("maida additional");
  const isFeed = nameToTest.includes("feed");
  const isBranQAP = nameToTest.includes("bran qap");
  const isBran = nameToTest.includes("bran") && !isBranQAP;
  const isRassi = nameToTest.includes("rassi");
  const isAtta = nameToTest.includes("atta");
  const isSooji = nameToTest.includes("sooji");
  const isFlakes = nameToTest.includes("flakes");
  const isChennai = nameToTest.includes("chennai");
  const isChennai1Percent = isChennai && (nameToTest.includes("1%") || nameToTest.includes("1 %") || nameToTest.includes("1 percent"));
  const isSakthivel = nameToTest.includes("sakthivel");
  const isSudhakar = nameToTest.includes("sudhakar");
  const isGeneral = nameToTest.includes("general");

  const getEligibility = (row) => {
    const salesObj = parseValue(row.original.monthSales);
    const targetObj = parseValue(row.original.targetMts);
    const from = row.original.achievedSlabFrom || 0;
    const to = row.original.achievedSlabTo || 0;

    if (isFeed) {
      return !(from === 0 && to === 0);
    } else if (isMaida) {
      return isMaidaEligible(salesObj, targetObj, isMaidaAdditional);
    } else {
      const sales = getSum(salesObj);
      const min = Number(row.original.minMts || 0);
      const max = Number(row.original.maxMts || 0);
      const tgt = getSum(targetObj);
      if (min > 0 && max > 0) return sales >= min || sales >= max;
      else if (tgt > 0) return sales >= tgt;
    }
    return false;
  };

  const baseCategories = ["PA", "PB", "BA", "BB", "MUL", "TOT"];

  // 👇 Updated Rate Cell to handle both per_ton and per_kg dynamically
  const EditableRateCell = ({ row, formik, setEditingRowId, realIndex, fieldKey = "new_scheme_rs_per_ton" }) => {
    const [localValue, setLocalValue] = useState(
      formik.values.childData[realIndex]?.[fieldKey] ?? ""
    );

    useEffect(() => {
      setLocalValue(formik.values.childData[realIndex]?.[fieldKey] ?? "");
    }, [formik.values.childData[realIndex]?.[fieldKey]]);

    const handleSave = () => {
      const newRate = parseFloat(localValue) || 0;
      const salesVolume = getSum(row.original.monthSales);
      const newAmount = salesVolume * newRate;

      formik.setFieldValue(`childData[${realIndex}].${fieldKey}`, newRate);
      formik.setFieldValue(`childData[${realIndex}].new_scheme_amount`, Math.round(newAmount));
      setEditingRowId(null);
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px' }}>
        <TextInput
          size="small"
          type="number"
          autoFocus
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
          sx={{ width: '90px' }}
        />
        <IconButton size="small" color="success" onClick={handleSave}>
          <CheckIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };

  const getRowHighlightProps = ({ row }) => {
    const eligible = getEligibility(row);
    const realIndex = formik.values.childData.findIndex(
      (r) => getCId(r) === getCId(row.original)
    );
    const isSelected = formik.values.childData[realIndex]?.selected == 1;

    if (eligible || isSelected) {
      return {
        sx: {
          backgroundColor: "#1f4e23 !important",
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: "#3b498a !important",
          },
        },
      };
    }
    return {};
  };

  const autoEligibleStyle = { border: "2px solid #4caf50", background: "#e8f5e9", borderRadius: "6px" };
  const userEligibleStyle = { border: "2px solid #1976d2", background: "#8cb8eb", borderRadius: "6px" };
  const userNotEligibleStyle = { border: "2px solid #d32f2f", background: "#fdecea", borderRadius: "6px" };

  const columns = [
    {
      id: "select",
      header: () => {
        // Use filteredData directly and only fallback to full list if it's explicitly null/undefined
        const currentViewData = (filteredData !== undefined && filteredData !== null) ? filteredData : (formik.values.childData || []);
        const isAllSelected = currentViewData.length > 0 && currentViewData.every((row) => Number(row.selected) === 1);
        const isSomeSelected = currentViewData.some((row) => Number(row.selected) === 1);

        return (
          <Checkbox
            checked={isAllSelected}
            indeterminate={isSomeSelected && !isAllSelected}
            onChange={(e) => {
              const newValue = e.target.checked ? 1 : 0;
              const viewCodes = new Set(currentViewData.map(r => getCId(r)));

              const updatedData = (formik.values.childData || []).map((row) => {
                if (!viewCodes.has(getCId(row))) return row;

                const salesObj = parseValue(row.monthSales);
                const targetObj = parseValue(row.targetMts);
                const from = row.achievedSlabFrom || 0;
                const to = row.achievedSlabTo || 0;

                let isRowEligible = false;
                if (isFeed) {
                  isRowEligible = !(from === 0 && to === 0);
                } else if (isMaida) {
                  isRowEligible = isMaidaEligible(salesObj, targetObj, isMaidaAdditional);
                } else {
                  const sales = getSum(salesObj);
                  const min = Number(row.minMts || 0);
                  const max = Number(row.maxMts || 0);
                  const tgt = getSum(targetObj);
                  if (min > 0 && max > 0) isRowEligible = sales >= min || sales >= max;
                  else if (tgt > 0) isRowEligible = sales >= tgt;
                }

                return {
                  ...row,
                  selected: newValue,
                  autoSelected: (isRowEligible && newValue === 1) ? 1 : 0,
                  userRejected: (isRowEligible && newValue === 0) ? 1 : 0,
                };
              });
              formik.setFieldValue("childData", updatedData);
            }}
          />
        );
      },
      muiTableBodyCellProps: getRowHighlightProps,
      cell: ({ row }) => {
        const salesObj = parseValue(row.original.monthSales);
        const targetObj = parseValue(row.original.targetMts);
        const from = row.original.achievedSlabFrom || 0;
        const to = row.original.achievedSlabTo || 0;

        let eligible = false;
        if (isFeed) {
          eligible = !(from === 0 && to === 0);
        } else if (isMaida) {
          eligible = isMaidaEligible(salesObj, targetObj, isMaidaAdditional);
        } else {
          const sales = getSum(salesObj);
          const min = Number(row.original.minMts || 0);
          const max = Number(row.original.maxMts || 0);
          const tgt = getSum(targetObj);
          if (min > 0 && max > 0) eligible = sales >= min || sales >= max;
          else if (tgt > 0) eligible = sales >= tgt;
        }

        const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
        if (realIndex === -1) return null;

        const rowPath = `childData[${realIndex}]`;
        const rowData = formik.values.childData[realIndex] || {};
        const isSelected = Number(rowData.selected) === 1;

        let style = {};
        if (eligible && isSelected) style = autoEligibleStyle;
        else if (!eligible && isSelected) style = userEligibleStyle;
        else if (eligible && !isSelected) style = userNotEligibleStyle;
        else style = { padding: 4 };

        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4px", height: "100%", ...style }}>
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                const newValue = e.target.checked ? 1 : 0;
                formik.setFieldValue(`${rowPath}.selected`, newValue);

                if (eligible && newValue === 0) {
                  formik.setFieldValue(`${rowPath}.autoSelected`, 0);
                  formik.setFieldValue(`${rowPath}.userRejected`, 1);
                }
                if (!eligible && newValue === 1) {
                  formik.setFieldValue(`${rowPath}.autoSelected`, 0);
                }
              }}
            />
          </div>
        );
      },
    },
    {
      header: "s-no",
      id: "s_no",
      accessorKey: "id",
      muiTableBodyCellProps: getRowHighlightProps,
      cell: ({ row }) => row.index + 1,
    },
    {
      header: "customer-code",
      accessorFn: (row) => getCId(row),
      id: "customer_code",
      muiTableBodyCellProps: getRowHighlightProps
    },
    {
      header: "customer-name",
      accessorFn: (row) => getCName(row),
      id: "customer_name",
      muiTableBodyCellProps: getRowHighlightProps
    },
  ];

  /* ==========================================================
      PRODUCT VIEWS (MUTUALLY EXCLUSIVE)
  ========================================================== */

  if (isFeed && !isBranQAP) {
    columns.push(
      {
        header: "sales-mts",
        accessorKey: "monthSales",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => {
          const from = row.original.achievedSlabFrom || 0;
          const to = row.original.achievedSlabTo || 0;
          const color = (from === 0 && to === 0) ? "red" : "green";
          return (
            <div style={{ textAlign: "right", fontWeight: 600, color }}>
              {indianNumberFormatComponent(row.original.monthSales || 0)}
            </div>
          );
        },
      },
      {
        header: "slab-achievedSlabFrom-achievedSlabTo",
        accessorKey: "achievedSlabFrom",
        accessorKey1: "achievedSlabTo",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => {
          const from = row.original.achievedSlabFrom || 0;
          const to = row.original.achievedSlabTo || 0;
          const color = (from === 0 && to === 0) ? "red" : "green";

          return (
            <div style={{ textAlign: "center", width: '130px', fontWeight: 600, color }}>
              {from} - {to}
            </div>
          );
        },
      },
      {
        header: "edit-slab",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const rowData = formik.values.childData[realIndex];
          const isEditing = editingRowId === `slab_${row.id}`;

          if (isEditing) {
            return <EditableSlabDropdown rowData={rowData} realIndex={realIndex} formik={formik} setEditingRowId={setEditingRowId} />;
          }

          const from = rowData.newAchievedSlabFrom !== undefined && rowData.newAchievedSlabFrom !== null
            ? rowData.newAchievedSlabFrom
            : "";
          const to = rowData.newAchievedSlabTo !== undefined && rowData.newAchievedSlabTo !== null
            ? rowData.newAchievedSlabTo
            : "";
          const color = (from === 0 && to === 0) ? "red" : "blue";

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <div style={{ textAlign: "center", width: '70px', fontWeight: 600, color }}>
                {from !== "" && to !== "" ? `${from} - ${to}` : ""}
              </div>
              <IconButton size="small" color="primary" onClick={() => setEditingRowId(`slab_${row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        header: "achieved-bags",
        accessorKey: "bagsAchieved",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {indianNumberFormatComponent(row.original.bagsAchieved || 0)}
          </div>
        ),
      },
      {
        header: "cost-per-ton",
        accessorKey: "costPerTon",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => (
          <div style={{ textAlign: "right", fontWeight: 600 }}>
            {indianNumberFormatComponent(row.original.costPerTon || 0)}
          </div>
        )
      },
      {
        header: "edit-cost-per-ton",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const isEditing = editingRowId === `rate_${row.id}`;
          const currentRate = formik.values.childData[realIndex]?.new_scheme_rs_per_ton;

          if (isEditing) {
            return <EditableRateCell row={row} formik={formik} setEditingRowId={setEditingRowId} realIndex={realIndex} fieldKey="new_scheme_rs_per_ton" />;
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
              <div style={{ fontWeight: 600, color: "#d32f2f" }}>
                {currentRate ? indianNumberFormatComponent(currentRate) : ""}
              </div>
              <IconButton size="small" color="primary" onClick={() => setEditingRowId(`rate_${row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => {
          const salesVolume = getSum(row.original.monthSales);
          const originalCost = row.original.costPerTon || 0;
          const originalAmount = Math.round(salesVolume * originalCost);

          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {indianNumberFormatComponent(originalAmount)}
            </div>
          );
        }
      },
      {
        header: "edit-scheme-amount",
        muiTableBodyCellProps: getRowHighlightProps,
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const currentAmount = formik.values.childData[realIndex]?.new_scheme_amount;

          return (
            <div style={{ textAlign: "right", fontWeight: 700, color: "#100979ff" }}>
              {currentAmount ? indianNumberFormatComponent(currentAmount) : ""}
            </div>
          );
        }
      }
    );
  }

  if (isMaida) {
    const gridBorderStyle = { borderRight: "1px solid #e0e0e0" };
    columns.push(
      {
        id: "target_group",
        header: "Target",
        muiTableHeadCellProps: { sx: { ...gridBorderStyle, justifyContent: "center", fontWeight: 700 } },
        columns: baseCategories.map((cat) => ({
          id: `target-${cat}`,
          header: cat,
          muiTableHeadCellProps: { sx: { ...gridBorderStyle, justifyContent: "center" } },
          muiTableBodyCellProps: ({ row }) => ({ sx: { ...gridBorderStyle, textAlign: "right", ...getRowHighlightProps({ row }).sx } }),
          accessorFn: (row) => cat === "TOT" ? getSum(row.targetMts) : getCategoryValue(row.targetMts, cat),
          cell: (info) => indianNumberFormatComponent(info.getValue()),
        })),
      },
      {
        id: "sales_group",
        header: "sales-mts",
        muiTableHeadCellProps: { sx: { ...gridBorderStyle, justifyContent: "center", fontWeight: 700 } },
        columns: baseCategories.map((cat) => ({
          id: `sales-${cat}`,
          header: cat,
          muiTableHeadCellProps: { sx: { ...gridBorderStyle, justifyContent: "center" } },
          muiTableBodyCellProps: ({ row }) => ({ sx: { ...gridBorderStyle, textAlign: "right", ...getRowHighlightProps({ row }).sx } }),
          accessorFn: (row) => cat === "TOT" ? getSum(row.monthSales) : getCategoryValue(row.monthSales, cat),
          cell: (info) => {
            const sales = info.getValue();
            const target = cat === "TOT" ? getSum(info.row.original.targetMts) : getCategoryValue(info.row.original.targetMts, cat);

            let color = "#f9a825"; // Default (target 0)
            if (target > 0) {
              if (isMaidaAdditional) {
                color = sales > target ? "green" : "red";
              } else {
                color = sales >= target ? "green" : "red";
              }
            }
            return <span style={{ color, fontWeight: 600 }}>{indianNumberFormatComponent(sales)}</span>;
          },
        })),
      },
      {
        id: "rs-per-ton",
        header: () => <div style={{ textAlign: "right", fontWeight: 700 }}>RS - Per KG/Ton</div>,
        columns: baseCategories.filter((cat) => cat !== "TOT").map((cat) => ({
          id: `budgeted-${cat}`,
          header: cat,
          cell: ({ row }) => {
            const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
            if (realIndex === -1) return null;
            return <EditableRateInput rowData={formik.values.childData[realIndex]} cat={cat} realIndex={realIndex} formik={formik} isMaidaAdditional={isMaidaAdditional} />;
          },
        })),
      },
      {
        id: "scheme-amount",
        header: () => <div style={{ textAlign: "right", fontWeight: 700 }}>Scheme Amount</div>,
        columns: baseCategories.map((cat) => ({
          id: `eligible-${cat}`,
          header: cat,
          cell: ({ row }) => {
            const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
            if (realIndex === -1) return null;
            const rowData = formik.values.childData[realIndex];

            if (cat === "TOT") {
              const totalAmount = getSum(rowData.categoryEligibleAmounts);
              return <div style={{ textAlign: "right", fontWeight: 600, color: "#100979ff" }}>{indianNumberFormatComponent(totalAmount)}</div>;
            }
            return <EditableAmountInput rowData={rowData} cat={cat} realIndex={realIndex} formik={formik} isMaidaAdditional={isMaidaAdditional} />;
          },
        })),
      },
    );
  }

  if (isSooji) {
    columns.push(
      {
        header: "target",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {indianNumberFormatComponent(getSum(row.original.targetMts))}
          </div>
        )
      },
      {
        header: "sales-mts",
        cell: ({ row }) => {
          const sales = getSum(row.original.monthSales);
          const target = getSum(row.original.targetMts);
          let color = target === 0 ? "#f9a825" : (sales < target ? "red" : "green");
          return (
            <div style={{ textAlign: "right", fontWeight: 600, color }}>
              {indianNumberFormatComponent(sales)}
            </div>
          )
        }
      },
      {
        header: "rs-per-kg",
        cell: ({ row }) => (
          <div style={{ textAlign: "right", fontWeight: 600, color: "#666" }}>
            {row.original.schemeRsPerKg || '0'}
          </div>
        ),
      },
      {
        header: "edit-rs-per-kg",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const isEditing = editingRowId === `rate_${row.id}`;
          const currentRate = formik.values.childData[realIndex]?.new_scheme_rs_per_kg ?? row.original.schemeRatePerBag;

          if (isEditing) {
            return <EditableRateCell row={row} formik={formik} setEditingRowId={setEditingRowId} realIndex={realIndex} fieldKey="new_scheme_rs_per_kg" />;
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '90px', fontWeight: 600 }}>{indianNumberFormatComponent(currentRate)}</div>
              <IconButton size="small" color="primary" onClick={() => setEditingRowId(`rate_${row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600, color: "#666" }}>
            {indianNumberFormatComponent(row.original.schemeNewEligibleAmount)}
          </div>
        ),
      },
      {
        header: "new-scheme-amount",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const newAmt = (formik.values.childData[realIndex]?.new_scheme_amount) * 1000 || 0;
          return (
            <div style={{ fontWeight: 700, color: '#09069E', textAlign: "center" }}>
              {newAmt ? indianNumberFormatComponent(newAmt) : ''}
            </div>
          );
        }
      }
    );
  }

  if (isBranQAP && !isMaida) {
    columns.push(
      {
        header: "target",
        cell: ({ row }) => {
          const min = parseFloat(row.original.minMts || 0);
          const max = parseFloat(row.original.maxMts || 0);
          const target = parseFloat(row.original.targetMts || 0);
          if (min && max) {
            return (
              <div style={{ textAlign: "center", fontWeight: 600 }}>
                {indianNumberFormatComponent(min)} – {indianNumberFormatComponent(max)}
              </div>
            );
          }
          return <div style={{ textAlign: "center", fontWeight: 600 }}>{indianNumberFormatComponent(target)}</div>;
        },
      },
      {
        header: "sales-mts",
        cell: ({ row }) => {
          const sales = getSum(row.original.monthSales);
          const target = getSum(row.original.targetMts);
          let color = target === 0 ? "#f9a825" : (sales < target ? "red" : "green");
          return (
            <div style={{ textAlign: "right", fontWeight: 600, color }}>
              {indianNumberFormatComponent(sales)}
            </div>
          )
        }
      },
      {
        header: "rs-per-kg",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600, color: "#666" }}>
            {row.original.schemeRsPerKg || '0'}
          </div>
        ),
      },
      {
        header: "edit-rs-per-kg",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const isEditing = editingRowId === `rate_${row.id}`;
          const currentRate = formik.values.childData[realIndex]?.new_scheme_rs_per_kg;

          if (isEditing) {
            return <EditableRateCell row={row} formik={formik} setEditingRowId={setEditingRowId} realIndex={realIndex} fieldKey="new_scheme_rs_per_kg" />;
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '90px', fontWeight: 600 }}>{currentRate}</div>
              <IconButton size="small" color="primary" onClick={() => setEditingRowId(`rate_${row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => {
          const schemeNewEligibleAmount = parseFloat(row.original.schemeNewEligibleAmount || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 500, color: "#100979ff" }}>
              {indianNumberFormatComponent(schemeNewEligibleAmount)}
            </div>
          );
        }
      },
      {
        header: "new-scheme-amount",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const newAmt = formik.values.childData[realIndex]?.new_scheme_amount * 1000;
          return (
            <div style={{ fontWeight: 700, color: '#09069E', textAlign: "center" }}>
              {newAmt ? indianNumberFormatComponent(newAmt) : ""}
            </div>
          );
        }
      }
    );
  }

  if (isBran && !isFlakes && !isMaida && !isBranQAP) {
    columns.push(
      {
        header: "target-slab",
        cell: ({ row }) => {
          const min = parseFloat(row.original.minMts || 0);
          const max = parseFloat(row.original.maxMts || 0);
          return (
            <div style={{ textAlign: "center", fontWeight: 600, width: '90px' }}>
              {indianNumberFormatComponent(min)} – {indianNumberFormatComponent(max)}
            </div>
          );
        },
      },
      {
        header: "sales-mts",
        cell: ({ row }) => {
          const sales = getSum(row.original.monthSales);
          const target = getSum(row.original.targetMts) || parseFloat(row.original.minMts) || 0;
          let color = target === 0 ? "#f9a825" : sales >= target ? "green" : "red";
          return (
            <div style={{ textAlign: "right", color, fontWeight: 600 }}>
              {indianNumberFormatComponent(sales)}
            </div>
          );
        }
      },
      {
        header: "slab-rs-per-ton",
        cell: ({ row }) => {
          const min = parseFloat(row.original.minRsPerKg || 0);
          const max = parseFloat(row.original.maxRsPerKg || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600, width: '90px' }}>
              {indianNumberFormatComponent(min)} – {indianNumberFormatComponent(max)}
            </div>
          );
        },
      },
      {
        header: "edit-rs-per-ton",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const isEditing = editingRowId === `rate_${row.id}`;
          const currentRate = formik.values.childData[realIndex]?.new_scheme_rs_per_ton;

          if (isEditing) {
            return <EditableRateCell row={row} formik={formik} setEditingRowId={setEditingRowId} realIndex={realIndex} fieldKey="new_scheme_rs_per_ton" />;
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '30px', fontWeight: 600 }}>{currentRate}</div>
              <IconButton size="small" color="primary" onClick={() => setEditingRowId(`rate_${row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600, color: "#666" }}>
            {indianNumberFormatComponent(row.original.schemeNewEligibleAmount)}
          </div>
        ),
      },
      {
        header: "new-scheme-amount",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const newAmt = formik.values.childData[realIndex]?.new_scheme_amount;
          return (
            <div style={{ fontWeight: 700, color: '#09069E', textAlign: "center" }}>
              {newAmt ? indianNumberFormatComponent(newAmt) : ""}
            </div>
          );
        }
      }
    );
  }
  if (isFlakes && !isBran && !isMaida && !isBranQAP) {
    columns.push(
      {
        header: "target-slab",
        cell: ({ row }) => {
          const min = parseFloat(row.original.minMts || 0);
          const max = parseFloat(row.original.maxMts || 0);
          return (
            <div style={{ textAlign: "center", fontWeight: 600, width: '90px' }}>
              {indianNumberFormatComponent(min)} – {indianNumberFormatComponent(max)}
            </div>
          );
        },
      },
      {
        header: "sales-mts",
        cell: ({ row }) => {
          const sales = getSum(row.original.monthSales);
          const target = getSum(row.original.targetMts) || parseFloat(row.original.minMts) || 0;
          let color = target === 0 ? "#f9a825" : sales >= target ? "green" : "red";
          return (
            <div style={{ textAlign: "right", color, fontWeight: 600 }}>
              {indianNumberFormatComponent(sales)}
            </div>
          );
        }
      },
      {
        header: "slab-rs-per-ton",
        cell: ({ row }) => {
          const min = parseFloat(row.original.minSchemeMts || 0);
          const max = parseFloat(row.original.maxSchemeMts || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600, width: '90px' }}>
              {indianNumberFormatComponent(min)} – {indianNumberFormatComponent(max)}
            </div>
          );
        },
      },
      {
        header: "edit-rs-per-ton",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const isEditing = editingRowId === `rate_${row.id}`;
          const currentRate = formik.values.childData[realIndex]?.new_scheme_rs_per_ton;

          if (isEditing) {
            return <EditableRateCell row={row} formik={formik} setEditingRowId={setEditingRowId} realIndex={realIndex} fieldKey="new_scheme_rs_per_ton" />;
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '30px', fontWeight: 600 }}>{currentRate}</div>
              <IconButton size="small" color="primary" onClick={() => setEditingRowId(`rate_${row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600, color: "#666" }}>
            {indianNumberFormatComponent(row.original.schemeNewEligibleAmount)}
          </div>
        ),
      },
      {
        header: "new-scheme-amount",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const newAmt = formik.values.childData[realIndex]?.new_scheme_amount;
          return (
            <div style={{ fontWeight: 700, color: '#09069E', textAlign: "center" }}>
              {newAmt ? indianNumberFormatComponent(newAmt) : ""}
            </div>
          );
        }
      }
    );
  }

  if (isChennai) {
    columns.push(
      {
        header: "tl-name",
        cell: ({ row }) => {
          return <div style={{ textAlign: "center", fontWeight: 200, width: '150px' }}>{row.original.brand}</div>;
        },
      },
      {
        header: "sales-mts",
        cell: ({ row }) => {
          return <div style={{ textAlign: "center", color: '#079d34ff', fontWeight: 600, width: '90px' }}>
            {indianNumberFormatComponent(row.original.monthSales)}</div>;
        },
      },

      {
        header: "basic-value",
        cell: ({ row }) => {
          return <div style={{ textAlign: "right", color: '#2c0251ff', fontWeight: 600, width: '90px' }}>
            {row.original.baseValue}</div>;
        },
      },

      {
        header: isChennai1Percent ? "one-percent-discount" : "discount",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", color: '#694304ff', fontWeight: 600, color: "#666" }}>
            {row.original.discountPercentage}
          </div>
        ),
      },
      {
        header: "invoice-discount",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", color: '#330114ff', fontWeight: 600, color: "#666" }}>
            {row.original.invoiceDiscount}
          </div>
        ),
      },
      {
        header: "balance-discount-value",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", color: '#01530eff', fontWeight: 600, color: "#666" }}>
            {row.original.discountedValue}
          </div>
        ),
      }
    );
  }
  if (isSakthivel) {
    columns.push(
      {
        header: "mts",
        cell: ({ row }) => {
          const sales = parseFloat(row.original.monthSales || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {indianNumberFormatComponent(sales)}
            </div>
          );
        },
      },
      {
        header: "scheme-rs-per-ton",
        cell: ({ row }) => (
          <div style={{ textAlign: "right", fontWeight: 600 }}>
            {row.original.schemeRsPerTon || 0}
          </div>
        ),
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => (
          <div style={{ textAlign: "right", fontWeight: 600 }}>
            {indianNumberFormatComponent(row.original.schemeNewEligibleAmount || 0)}
          </div>
        ),
      }
    );
  }
  if (isSudhakar) {
    columns.push(
      {
        header: "no-of-bags",
        cell: ({ row }) => {
          const bags = parseFloat(row.original.noOfBags || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {indianNumberFormatComponent(bags)}
            </div>
          );
        },
      },
      {
        header: "mts",
        cell: ({ row }) => {
          const sales = parseFloat(row.original.monthSales || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {indianNumberFormatComponent(sales)}
            </div>
          );
        },
      },
      {
        header: "rs-per-bag",
        cell: ({ row }) => {
          const rsPerBag = parseFloat(row.original.rsPerBag || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {rsPerBag}
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => (
          <div style={{ textAlign: "right", fontWeight: 600 }}>
            {indianNumberFormatComponent(row.original.schemeValue || 0)}
          </div>
        ),
      }
    );
  }

  if (isGeneral) {
    columns.push(
      {
        header: "no-of-bags",
        cell: ({ row }) => {
          const bags = parseFloat(row.original.noOfBags || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {indianNumberFormatComponent(bags)}
            </div>
          );
        },
      },
      {
        header: "mts",
        cell: ({ row }) => {
          const sales = parseFloat(row.original.monthSales || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {indianNumberFormatComponent(sales)}
            </div>
          );
        },
      },
      {
        header: "rs-per-kg",
        cell: ({ row }) => {
          const rsPerKg = parseFloat(row.original.schemeRsPerKg || 0);
          return (
            <div style={{ textAlign: "right", fontWeight: 600 }}>
              {rsPerKg}
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => (
          <div style={{ textAlign: "right", fontWeight: 600 }}>
            {indianNumberFormatComponent(row.original.schemeValue || 0)}
          </div>
        ),
      }
    );
  }
  /* ✅ DEFAULT / OTHER PRODUCTS VIEW */
  if (isRassi || isAtta && !isMaida && !isBranQAP) {
    columns.push(
      {
        header: "target",
        cell: ({ row }) => {
          const target = parseFloat(row.original.targetMts || 0);
          return <div style={{ textAlign: "center", fontWeight: 600, width: '90px' }}>{indianNumberFormatComponent(target)}</div>;
        },
      },
      {
        header: "sales-mts",
        cell: ({ row }) => {
          const sales = getSum(row.original.monthSales);
          const target = getSum(row.original.targetMts) || parseFloat(row.original.minMts) || 0;
          let color = target === 0 ? "#f9a825" : sales >= target ? "green" : "red";
          return (
            <div style={{ textAlign: "right", color, fontWeight: 600 }}>
              {indianNumberFormatComponent(sales)}
            </div>
          );
        }
      },
      {
        header: "rs-per-ton",
        cell: ({ row }) => {
          const target = parseFloat(row.original.schemeRsPerTon || row.original.schemeRatePerBag);
          return <div style={{ textAlign: "right", fontWeight: 600, width: '90px' }}>{indianNumberFormatComponent(target)}</div>;
        },
      },
      {
        header: "edit-rs-per-ton",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const isEditing = editingRowId === `rate_${row.id}`;
          const currentRate = formik.values.childData[realIndex]?.new_scheme_rs_per_ton;

          if (isEditing) {
            return <EditableRateCell row={row} formik={formik} setEditingRowId={setEditingRowId} realIndex={realIndex} fieldKey="new_scheme_rs_per_ton" />;
          }
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '30px', fontWeight: 600 }}>{currentRate}</div>
              <IconButton size="small" color="primary" onClick={() => setEditingRowId(`rate_${row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        header: "scheme-amount",
        cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600, color: "#666" }}>
            {indianNumberFormatComponent(row.original.schemeNewEligibleAmount)}
          </div>
        ),
      },
      {
        header: "new-scheme-amount",
        cell: ({ row }) => {
          const realIndex = formik.values.childData.findIndex((r) => getCId(r) === getCId(row.original));
          if (realIndex === -1) return null;

          const newAmt = formik.values.childData[realIndex]?.new_scheme_amount;
          return (
            <div style={{ fontWeight: 700, color: '#09069E', textAlign: "center" }}>
              {newAmt ? indianNumberFormatComponent(newAmt) : ""}
            </div>
          );
        }
      }
    );
  }

  /* ==========================================================
     COMMON EXTRA COLUMNS (Only applied to non-Feed products)
  ========================================================== */
  if (!isFeed && !isChennai && !isSakthivel && !isSudhakar && !isGeneral) {
    if (handleActionChange && actionOptions) {
      columns.push({
        header: "average-sales",
        accessorKey: "avgSales",
        cell: ({ row }) => (
          <TextInput
            size="small"
            fullWidth
            value={vadDataUpdate(row.original.avgSales, row.original.avgSales_index)}
            InputProps={{ readOnly: true }}
          />
        ),
      });
    }

    if (handleAvgRuleChange && averageRuleOptions) {
      columns.push({
        header: "Average Rule",
        cell: ({ row }) => (
          <Dropdown
            size="small"
            formik={formik}
            options={averageRuleOptions}
            value={vadDataUpdate(row.original.avgRule, row.original.avgRule_index)}
            sx={{ width: 250 }}
            onChange={(e) => handleAvgRuleChange(e, row.original)}
          />
        ),
      });
    }

    if (handleButtonClick) {
      columns.push({
        header: "Fetch",
        cell: ({ row }) => {
          const isRowLoading = row.original.isRowLoading === true;
          return (
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={isRowLoading}
              onClick={() => handleButtonClick(row.original)}
              startIcon={isRowLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isRowLoading ? "..." : "Get Sales"}
            </Button>
          );
        },
      });
    }
  }

  if (!isChennai && !isSakthivel && !isSudhakar && !isGeneral) {
    columns.push({
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => {
        const salesObj = parseValue(row.original.monthSales);
        const targetObj = parseValue(row.original.targetMts);
        const from = row.original.achievedSlabFrom || 0;
        const to = row.original.achievedSlabTo || 0;

        if (isFeed) {
          const eligible = !(from === 0 && to === 0);
          return eligible ? <Chip size="small" color="success" label="Eligible" /> : <Chip size="small" color="error" label="Not Eligible" />;
        }

        let eligible = false;
        if (isMaida) {
          eligible = isMaidaEligible(salesObj, targetObj, isMaidaAdditional);
        } else {
          const sales = getSum(salesObj);
          const min = parseFloat(row.original.minMts || 0);
          const max = parseFloat(row.original.maxMts || 0);
          const target = getSum(targetObj);

          if (min && max) eligible = sales >= min || sales >= max;
          else if (target) eligible = sales >= target;
        }

        if (!getSum(salesObj) && !getSum(targetObj)) return <Chip size="small" label="-" />;
        return eligible ? <Chip size="small" color="success" label="Eligible" /> : <Chip size="small" color="error" label="Not Eligible" />;
      },
    });
  }

  return columns;
};

/* -------------------------------- EXPORTS -------------------------------- */

export const anotherColumns = (props) => [
  { id: "select" },
  ...commonColumns(props),
];

export const getInitialValues = (apiData = {}) => ({
  activeStatus: 1,
  ...apiData,
  childData: (apiData.childData || []).map(row => ({
    ...row,
    selected: row.selected ?? undefined,
    autoSelected: row.autoSelected ?? 0,
  })),
});

export const initialValues = {
  activeStatus: 1,
};